import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Trophy, 
  Users, 
  Clock,
  Medal,
  Lock,
  Unlock,
  Sparkles,
  Loader,
  CheckCircle
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import healthApi from '@/api/healthApi';

const CommunityHub = () => {
  const [challenges, setChallenges] = useState([]);
  const [userProgress, setUserProgress] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [motivationalMessages, setMotivationalMessages] = useState([]);
  const [privacySetting, setPrivacySetting] = useState('private');
  const [loading, setLoading] = useState(true);
  const [hasJoined, setHasJoined] = useState(false);

  useEffect(() => {
    loadCommunityData();
  }, []);

  const loadCommunityData = async () => {
    try {
      setLoading(true);

      // Load active challenges
      const challengesRes = await healthApi.getActiveChallenges();
      const activeChallenges = challengesRes.data.challenges || [];
      setChallenges(activeChallenges);

      if (activeChallenges.length > 0) {
        const challengeId = activeChallenges[0].id;

        // Load user progress
        const progressRes = await healthApi.getChallengeProgress(challengeId);
        setUserProgress(progressRes.data);
        setHasJoined(progressRes.data.joined);

        if (progressRes.data.joined) {
          // Load leaderboard if user joined
          const leaderboardRes = await healthApi.getLeaderboard(challengeId);
          setLeaderboard(leaderboardRes.data.leaderboard || []);

          // Load motivational messages
          const messagesRes = await healthApi.getMotivationalMessages();
          setMotivationalMessages(messagesRes.data.messages || []);
        }
      }

      // Load privacy settings
      const settingsRes = await healthApi.getCommunitySettings();
      setPrivacySetting(settingsRes.data.privacy_level || 'private');

    } catch (error) {
      console.error('Error loading community data:', error);
      toast.error('Failed to load community features');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinChallenge = async () => {
    if (challenges.length === 0) return;

    try {
      const challengeId = challenges[0].id;
      await healthApi.joinChallenge(challengeId, privacySetting);
      toast.success('🎉 Welcome to the challenge!');
      setHasJoined(true);
      await loadCommunityData();
    } catch (error) {
      console.error('Error joining challenge:', error);
      toast.error('Failed to join challenge');
    }
  };

  const handlePrivacyChange = async (newSetting) => {
    try {
      await healthApi.updatePrivacySettings(newSetting, newSetting === 'community');
      setPrivacySetting(newSetting);
      toast.success('Privacy settings updated');
      
      if (hasJoined) {
        await loadCommunityData();
      }
    } catch (error) {
      console.error('Error updating privacy:', error);
      toast.error('Failed to update settings');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (challenges.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="p-8 text-center">
          <Trophy className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">No Active Challenges</h2>
          <p className="text-gray-600">Check back on Monday for this week's challenge!</p>
        </Card>
      </div>
    );
  }

  const currentChallenge = challenges[0];
  const percentage = userProgress?.percentage || 0;
  const daysLeft = userProgress?.days_remaining || 0;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Trophy className="h-8 w-8 text-yellow-500" />
            Community Challenges
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Join the community and reach your health goals together!
          </p>
        </div>
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
          <Users className="h-3 w-3 mr-1" />
          {currentChallenge.participants_count} Participants
        </Badge>
      </div>

      {/* Privacy Settings */}
      <Card className="p-4 bg-amber-50 border-amber-200">
        <div className="flex items-start gap-3">
          <Lock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-sm text-amber-900 mb-1">
              Privacy Settings
            </h3>
            <p className="text-xs text-amber-800 mb-3">
              Choose your visibility level:
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={privacySetting === 'private' ? 'default' : 'outline'}
                onClick={() => handlePrivacyChange('private')}
                className="text-xs"
              >
                <Lock className="h-3 w-3 mr-1" />
                Private
              </Button>
              <Button
                size="sm"
                variant={privacySetting === 'community' ? 'default' : 'outline'}
                onClick={() => handlePrivacyChange('community')}
                className="text-xs"
              >
                <Unlock className="h-3 w-3 mr-1" />
                Community
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Main Challenge Card */}
      <Card className="p-6 bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200">
        <div className="space-y-4">
          {/* Challenge Header */}
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-purple-900 flex items-center gap-2">
                {currentChallenge.emoji} {currentChallenge.title}
              </h2>
              <p className="text-sm text-purple-700 mt-1">
                {currentChallenge.description}
              </p>
            </div>
            <Badge className="bg-purple-600 text-white">
              <Clock className="h-3 w-3 mr-1" />
              {daysLeft} days left
            </Badge>
          </div>

          {!hasJoined ? (
            /* Join Challenge CTA */
            <div className="text-center py-6">
              <p className="text-gray-700 mb-4">Join this week's challenge to start tracking your progress!</p>
              <Button
                onClick={handleJoinChallenge}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Join Challenge
              </Button>
            </div>
          ) : (
            /* Progress Display */
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700 font-medium">Your Progress</span>
                  <span className="text-purple-900 font-bold">
                    {userProgress.current_progress?.toLocaleString()} / {userProgress.goal_target?.toLocaleString()} {currentChallenge.goal.unit}
                  </span>
                </div>
                <Progress value={percentage} className="h-3" />
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <span>{percentage.toFixed(0)}% complete</span>
                  <span>{(userProgress.goal_target - userProgress.current_progress).toLocaleString()} to go</span>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 pt-2">
                <div className="text-center p-3 bg-white/60 rounded-lg">
                  <div className="text-2xl font-bold text-purple-900">
                    {userProgress.percentile || 0}%
                  </div>
                  <div className="text-xs text-gray-600 mt-1">Ahead of users</div>
                </div>
                <div className="text-center p-3 bg-white/60 rounded-lg">
                  <div className="text-2xl font-bold text-purple-900">
                    {Math.round(currentChallenge.completion_rate * 100)}%
                  </div>
                  <div className="text-xs text-gray-600 mt-1">Completion rate</div>
                </div>
                <div className="text-center p-3 bg-white/60 rounded-lg">
                  <div className="text-2xl font-bold text-purple-900">
                    {currentChallenge.participants_count}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">Participants</div>
                </div>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Motivational Messages */}
      {motivationalMessages.length > 0 && (
        <Card className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-green-900">
                {motivationalMessages[0].message}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Leaderboard */}
      {hasJoined && privacySetting === 'community' && leaderboard.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Medal className="h-5 w-5 text-yellow-500" />
              Leaderboard
            </h3>
            <span className="text-xs text-gray-500">Top performers this week</span>
          </div>

          <div className="space-y-2">
            {leaderboard.map((entry) => (
              <div
                key={entry.rank}
                className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 text-center font-bold text-gray-700">
                    {entry.rank <= 3 ? entry.badge : entry.rank}
                  </div>
                  <div className="font-medium text-sm text-gray-900">
                    {entry.display_name}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-gray-900">
                    {entry.progress.toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500">{currentChallenge.goal.unit}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default CommunityHub;
